import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { checkConnection } from "services/api";

type NetworkStatusContextValue = {
	isOnline: boolean;
	isVerifying: boolean;
	verifyConnection: () => Promise<boolean>;
};

const NetworkStatusContext = createContext<NetworkStatusContextValue | null>(null);

export const NetworkStatusProvider = ({ children }: { children: ReactNode }) => {
	const [isOnline, setIsOnline] = useState(window.navigator.onLine);
	const [isVerifying, setIsVerifying] = useState(false);
	const verifyPromiseRef = useRef<Promise<boolean> | null>(null);

	const verifyConnection = useCallback(async () => {
		if (verifyPromiseRef.current) {
			return verifyPromiseRef.current;
		}

		setIsVerifying(true);

		const promise = (async () => {
			try {
				await checkConnection();
				setIsOnline(true);
				return true;
			} catch {
				setIsOnline(false);
				return false;
			} finally {
				setIsVerifying(false);
				verifyPromiseRef.current = null;
			}
		})();

		verifyPromiseRef.current = promise;
		return promise;
	}, []);

	useEffect(() => {
		const handleOnline = () => {
			void verifyConnection();
		};

		const handleOffline = () => {
			setIsOnline(false);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, [verifyConnection]);

	const value = useMemo(
		() => ({ isOnline, isVerifying, verifyConnection }),
		[isOnline, isVerifying, verifyConnection],
	);

	return createElement(NetworkStatusContext.Provider, { value }, children);
};

export const useNetworkStatus = () => {
	const context = useContext(NetworkStatusContext);
	if (!context) {
		throw new Error("useNetworkStatus must be used within NetworkStatusProvider");
	}
	return context;
};
